import { Button, FormInput } from "@/components/bnp-ui";
import { PaperClip } from "@/components/bnp-icons";
import LegalNameNotice from "./LegalNameNotice";
import NameFields from "./NameFields";
import {
  ACCEPTED_FILE_FORMATS,
  MAX_FILE_SIZE_MB,
  getDocumentNumberRule,
} from "./constants";

interface AddDocumentFormProps {
  documentType: string;
  documentNumber: string;
  onDocumentNumberChange: (value: string) => void;
  firstName: string;
  onFirstNameChange: (value: string) => void;
  lastName: string;
  onLastNameChange: (value: string) => void;
  file: File | null;
  isUploading: boolean;
  uploadedFileUrl: string | null;
  onFileChange: (file: File | null) => void;
  onRemoveFile: () => void;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

const AddDocumentForm = ({
  documentType,
  documentNumber,
  onDocumentNumberChange,
  firstName,
  onFirstNameChange,
  lastName,
  onLastNameChange,
  file,
  isUploading,
  uploadedFileUrl,
  onFileChange,
  onRemoveFile,
  isPending,
  onCancel,
  onSubmit,
}: AddDocumentFormProps) => {
  const rule = getDocumentNumberRule(documentType);
  const isNumberInvalid =
    !!rule &&
    documentNumber.trim().length > 0 &&
    !rule.pattern.test(documentNumber.trim());

  return (
    <div className="bg-white p-7 rounded-[28px] shadow-[0_10px_35px_rgba(0,0,0,0.03)] border border-gray-200 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-slate-900 font-bold text-base mb-1">
            Upload A Document
          </h2>
          <p className="text-xs text-slate-400 leading-relaxed">
            Upload your {documentType.toUpperCase()} document for verification.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-xl bg-[#F4F6FA] text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors shrink-0"
        >
          Cancel
        </button>
      </div>

      <LegalNameNotice />

      <div className="space-y-4">
        <NameFields
          firstName={firstName}
          onFirstNameChange={onFirstNameChange}
          lastName={lastName}
          onLastNameChange={onLastNameChange}
        />

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
            Document type <span className="text-rose-500">*</span>
          </label>
          <div className="w-full h-12 rounded-xl bg-[#F8FAFC] border-none px-4 flex items-center text-sm font-semibold text-slate-700">
            {documentType ? documentType.toUpperCase() : "—"}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
            Document ID Number <span className="text-rose-500">*</span>
          </label>
          <FormInput
            type="text"
            name="documentNumber"
            value={documentNumber}
            placeholder={
              rule
                ? `Enter your ${documentType.toUpperCase()} number`
                : "Enter document identification number"
            }
            className="w-full h-12 rounded-xl bg-[#F8FAFC] border-none px-4 text-sm focus:bg-white focus:ring-2 focus:ring-[#153e46]/20 transition-all"
            onChange={(e) => onDocumentNumberChange(e.target.value)}
            maxLength={rule?.maxLength}
            inputMode={
              documentType.toLowerCase() === "nin" ? "numeric" : "text"
            }
            required
          />
          {rule && (
            <p
              className={`text-[11px] mt-0.5 ${
                isNumberInvalid
                  ? "text-rose-500 font-semibold"
                  : "text-slate-400"
              }`}
            >
              {isNumberInvalid ? rule.error : rule.hint}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
            Upload document file <span className="text-rose-500">*</span>
          </label>

          {!file ? (
            <label
              className={`relative flex flex-col items-center justify-center gap-2 min-h-[140px] rounded-2xl cursor-pointer transition-all ${
                isUploading
                  ? "bg-[#153e46]/10"
                  : "bg-[#F8FAFC] hover:bg-[#F1F5F9]"
              }`}
            >
              <input
                type="file"
                accept={ACCEPTED_FILE_FORMATS.join(",")}
                onChange={(e) => onFileChange(e.target.files?.[0] || null)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-[#153e46] shadow-xs">
                <PaperClip />
              </div>
              <span className="text-xs font-bold text-slate-700">
                {isUploading
                  ? "Uploading file..."
                  : "Click to select or drop document here"}
              </span>
              <span className="text-[11px] text-slate-400">
                {ACCEPTED_FILE_FORMATS.map((f) =>
                  f.split("/")[1]?.toUpperCase(),
                ).join(", ")}{" "}
                • up to {MAX_FILE_SIZE_MB}MB
              </span>
            </label>
          ) : (
            <div className="flex items-center justify-between bg-[#F8FAFC] p-4 rounded-2xl">
              <div className="flex items-center gap-3.5 min-w-0 overflow-hidden">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-[#153e46] shadow-xs shrink-0">
                  <PaperClip />
                </div>
                <div className="min-w-0 overflow-hidden">
                  <p className="text-xs font-bold text-slate-800 truncate">
                    {file.name}
                  </p>
                  <p className="text-[11px] text-slate-400">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onRemoveFile}
                disabled={isUploading}
                className="px-3.5 py-1.5 rounded-xl bg-rose-50 text-rose-600 text-xs font-semibold hover:bg-rose-100 transition-colors disabled:opacity-40 shrink-0 ml-3"
              >
                Remove
              </button>
            </div>
          )}

          {isUploading && (
            <div>
              <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#153e46] animate-pulse"
                  style={{ width: "65%" }}
                />
              </div>
              <p className="text-[11px] text-slate-400 mt-1.5">
                Uploading file, please wait...
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-end sm:items-center justify-end gap-3 pt-4 border-t border-slate-100">
        <p className="text-[11px] text-slate-400 sm:mr-auto text-left sm:text-right max-w-[260px]">
          Add more documents if needed — everything is submitted together with
          one final click.
        </p>
        <Button
          className="h-12 px-7 bg-[#153e46] hover:bg-[#0f2e34] text-white text-sm font-semibold rounded-2xl shadow-md shadow-[#153e46]/20 transition-all"
          onClick={onSubmit}
          disabled={
            isUploading ||
            !documentType ||
            !documentNumber.trim() ||
            isNumberInvalid ||
            !firstName.trim() ||
            !lastName.trim() ||
            !uploadedFileUrl ||
            isPending
          }
          isLoading={isPending}
        >
          Save document
        </Button>
      </div>
    </div>
  );
};

export default AddDocumentForm;
